import React from 'react';

import { Alert } from './common';
import Logo from './Logo';

// The v9 `login` screen for tenants whose accounts come from their admin.
interface BasysLoginFormProps {
	logoBase64: string | null;
	tenantName: string;
	email: string;
	setEmail: (email: string) => void;
	password: string;
	setPassword: (password: string) => void;
	loading: boolean;
	error: string;
	onSubmit: (e: React.FormEvent) => void;
	onForgotPassword: () => void;
	onGoogleLogin: () => void;
	isGoogleLoading: boolean;
	onMicrosoftLogin?: () => void;
	isMicrosoftLoading: boolean;
}

const inputClass =
	'block w-full border border-gray-300 rounded-lg bg-white px-4 py-2 text-sm leading-5 text-gray-900 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-2';
const providerClass =
	'flex items-center gap-[10px] w-full border border-gray-300 bg-white rounded-lg px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed';

const BasysLoginForm: React.FC<BasysLoginFormProps> = ({
	logoBase64,
	tenantName,
	email,
	setEmail,
	password,
	setPassword,
	loading,
	error,
	onSubmit,
	onForgotPassword,
	onGoogleLogin,
	isGoogleLoading,
	onMicrosoftLogin,
	isMicrosoftLoading,
}) => (
	<div className="min-h-screen bg-gray-50 flex items-start justify-center px-6 py-16 text-sm text-gray-900">
		<div className="w-full max-w-[440px]">
			<div className="flex justify-center mb-6">
				<Logo
					logoBase64={logoBase64}
					alt={tenantName}
					className="h-16 w-auto"
				/>
			</div>
			<h1 className="text-[28px] font-semibold text-center text-gray-900">
				Sign in to your account
			</h1>
			<p className="text-center text-gray-500 mt-2">
				Accounts are created by your {tenantName} admin.
			</p>
			<form
				className="bg-white border border-gray-200 rounded-lg px-10 py-8 mt-8 shadow-[0_4px_18px_rgba(0,6,102,0.08)]"
				onSubmit={onSubmit}
			>
				<div>
					<label htmlFor="email" className={labelClass}>
						Email address
					</label>
					<input
						id="email"
						name="email"
						type="email"
						autoComplete="email"
						required
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						className={inputClass}
					/>
				</div>
				<div className="mt-4">
					<label htmlFor="password" className={labelClass}>
						Password
					</label>
					<input
						id="password"
						name="password"
						type="password"
						autoComplete="current-password"
						required
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						className={inputClass}
					/>
				</div>
				{error && (
					<div className="mt-4">
						<Alert variant="error">{error}</Alert>
					</div>
				)}
				<button
					type="submit"
					disabled={loading}
					className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium leading-5 rounded-lg border border-transparent bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
				>
					Sign in
				</button>
				<div className="flex items-center gap-3 my-4 text-xs text-gray-500">
					<span className="flex-1 h-px bg-gray-200" />
					Or continue with
					<span className="flex-1 h-px bg-gray-200" />
				</div>
				<button
					type="button"
					onClick={onGoogleLogin}
					disabled={isGoogleLoading}
					className={providerClass}
				>
					<svg
						viewBox="0 0 48 48"
						aria-hidden="true"
						className="w-[18px] h-[18px] flex-none"
					>
						<path
							fill="#EA4335"
							d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
						/>
						<path
							fill="#4285F4"
							d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
						/>
						<path
							fill="#FBBC05"
							d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
						/>
						<path
							fill="#34A853"
							d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
						/>
					</svg>
					Sign in with Google
				</button>
				{onMicrosoftLogin && (
					<button
						type="button"
						onClick={onMicrosoftLogin}
						disabled={isMicrosoftLoading}
						className={`${providerClass} mt-2`}
					>
						<svg
							viewBox="0 0 21 21"
							aria-hidden="true"
							className="w-[18px] h-[18px] flex-none"
						>
							<rect x="1" y="1" width="9" height="9" fill="#f25022" />
							<rect x="11" y="1" width="9" height="9" fill="#7fba00" />
							<rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
							<rect x="11" y="11" width="9" height="9" fill="#ffb900" />
						</svg>
						Sign in with Microsoft
					</button>
				)}
				<div className="mt-4 text-xs text-right">
					<button
						type="button"
						onClick={onForgotPassword}
						className="text-primary-700"
					>
						Forgot password?
					</button>
				</div>
			</form>
			<p className="mt-6 text-xs text-gray-500 text-center">
				{window.location.hostname}
			</p>
		</div>
	</div>
);

export default BasysLoginForm;
